import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Form } from "react-bootstrap";
import { SectionCard } from "./SectionCard";
import {
    AddCustomSectionButton,
    CardSection,
    FieldLabel,
    FormInput,
    InfoCard,
    SectionField,
    ToggleContainer,
} from "./styles";
import { CustomQuestion, CustomSection, RequestFormState } from "./types";


const REQUIRED_FIELDS = [
    'Nome completo',
    'Nome da organização',
    'E-mail',
    'Telefone',
    'Data(s) início e fim',
    'Horário início e fim',
    'Quantidade de participantes',
    'Nome do evento: quando finalidade do espaço = evento',
    'Descrição do evento: quando finalidade do espaço = evento',
]


interface ApplicationFormsTabProps {
  formId?: string
  data: RequestFormState
  onChange: (data: RequestFormState) => void
}


export const createDefaultQuestion = (): CustomQuestion => ({
    text: '',
    description: '',
    type: 'short_text',
    required: false,
    hiddenFromEvaluators: false,
    expanded: true,
    options: [],
})

export const createDefaultSection = (): CustomSection => ({
    name: '',
    questions: [createDefaultQuestion()],
    expanded: true,
})

export const initialApplicationFormState: RequestFormState = {
    checkboxText: '',
    checkboxRequired: false,
    warningText: '',
    fixedFields: [],
    sections: [createDefaultSection()],
    fixedSectionExpanded: true,
}

export const ApplicationFormsTab = ({ formId, data, onChange }: ApplicationFormsTabProps): JSX.Element => {
    const updateSections = (updater: (sections: CustomSection[]) => CustomSection[]) => {
        onChange({ ...data, sections: updater(data.sections) })
    }

    const updateSection = (sectionIndex: number, updater: (section: CustomSection) => CustomSection) => {
        updateSections(prev => prev.map((s, i) => (i === sectionIndex ? updater(s) : s)))
    }

    const updateQuestion = (
        sectionIndex: number,
        questionIndex: number,
        updater: (question: CustomQuestion) => CustomQuestion,
    ) => {
        updateSection(sectionIndex, section => ({
            ...section,
            questions: section.questions.map((q, i) => (i === questionIndex ? updater(q) : q)),
        }))
    }

    const handleToggleSection = (sectionIndex: number) => {
        updateSection(sectionIndex, s => ({ ...s, expanded: !s.expanded }))
    }

    const handleRemoveSection = (sectionIndex: number) => {
        updateSections(prev => (prev.length > 0 ? prev.filter((_, i) => i !== sectionIndex) : prev))
    }

    const handleAddSection = () => {
        updateSections(prev => [...prev, createDefaultSection()])
    }

    return (
        <Form id={formId}>
            <InfoCard>
                <h4>Estes campos são obrigatórios</h4>
                <ul>
                    {REQUIRED_FIELDS.map((field, index) => <li key={index}>{field}</li>)}
                </ul>
            </InfoCard>

            {data.sections.map((section, sectionIndex) => (
                <SectionCard
                    key={section.id || sectionIndex}
                    section={section}
                    sectionIndex={sectionIndex}
                    onToggle={() => handleToggleSection(sectionIndex)}
                    onChange={updater => updateSection(sectionIndex, updater)}
                    onChangeQuestion={(questionIndex, updater) => updateQuestion(sectionIndex, questionIndex, updater)}
                    onRemove={() => handleRemoveSection(sectionIndex)}
                />
            ))}

            <AddCustomSectionButton
                type="button"
                onClick={handleAddSection}
                data-testid="add-section-button"
            >
                <FontAwesomeIcon icon={faPlus} />
                Adicionar seção
            </AddCustomSectionButton>

            <CardSection style={{ marginTop: 32 }}>
                <SectionField style={{ marginBottom: 16 }}>
                    <FieldLabel htmlFor="checkbox-text">Texto do checkbox de aceite</FieldLabel>
                    <FormInput
                        id="checkbox-text"
                        type="text"
                        value={data.checkboxText}
                        onChange={e => onChange({ ...data, checkboxText: e.target.value })}
                    />
                </SectionField>
                <ToggleContainer>
                    <Form.Check
                        type="switch"
                        id="checkbox-required"
                        label="obrigatório?"
                        checked={data.checkboxRequired}
                        onChange={e => onChange({ ...data, checkboxRequired: e.target.checked })}
                    />
                </ToggleContainer>
            </CardSection>

            <CardSection>
                <SectionField>
                    <FieldLabel htmlFor="warning-text">Aviso</FieldLabel>
                    <FormInput
                        id="warning-text"
                        type="text"
                        value={data.warningText}
                        onChange={e => onChange({ ...data, warningText: e.target.value })}
                    />
                </SectionField>
            </CardSection>
        </Form>
    );
};
